import React, { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CloudTypeEnum,
  type ProductType,
  useAccountId,
  useCloud,
} from '@labrav/common'

interface BaseRouteWithAccountRedirectProps {
  product: ProductType
  resource: string
}

export const BaseRouteWithAccountRedirect: React.FC<
  BaseRouteWithAccountRedirectProps
> = ({ product, resource }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const cloud = useCloud({ product })
  const accountId = useAccountId({ product, cloud })

  useEffect(() => {
    if (!accountId) {
      return
    }

    const currentPath = location.pathname
    const productPath = `/${product}/`
    const productIndex = currentPath.indexOf(productPath)

    if (productIndex === -1) {
      return
    }

    // Get everything before /<product>/
    const prefix = currentPath.substring(0, productIndex)
    // Get everything after /<product>/
    const afterProduct = currentPath.substring(
      productIndex + productPath.length
    )

    // Determine the cloud to use (from prop or default to AWS)
    // const cloudToUse = cloud || CloudType.AZURE
    const cloudToUse = cloud || CloudTypeEnum.AWS
    const cloudPath = cloudToUse.toLowerCase()

    // Check if the path already has a cloud segment (e.g., /aws/product-listing)
    const pathParts = afterProduct.split('/')
    const firstPart = pathParts[0]?.toLowerCase()

    // Common cloud paths to check
    const cloudPaths = ['aws', 'azure', 'gcp', 'redhat']
    const hasCloudSegment = cloudPaths.includes(firstPart)

    let newPath: string
    if (hasCloudSegment) {
      // Path already has cloud, insert accountId after it
      // e.g., /aws/product-listing -> /aws/<account_id>/product-listing
      const restOfPath = pathParts.slice(1).join('/')
      newPath = `${prefix}/${product}/${firstPart}/${accountId}${restOfPath ? `/${restOfPath}` : ''}`
    } else {
      // Path doesn't have cloud, insert cloud and accountId
      // e.g., /product-listing -> /aws/<account_id>/product-listing
      newPath = `${prefix}/${product}/${cloudPath}/${accountId}${afterProduct ? `/${afterProduct}` : ''}`
    }

    // Preserve search params
    const search = location.search

    if (currentPath !== newPath) {
      navigate(
        {
          pathname: newPath,
          search,
        },
        { replace: true }
      )
    }
  }, [
    accountId,
    product,
    cloud,
    resource,
    location.pathname,
    location.search,
    navigate,
  ])

  return null
}
